<template>
  <div>
    <!-- Success/Error Messages -->
    <div v-if="successMessage" class="alert alert-success" style="color: rgb(5, 20, 48)">
      {{ successMessage }}
    </div>
    <div v-if="errorMessage" class="alert alert-danger" style="color: red;">
      {{ errorMessage }}
    </div>

    <!-- Loading State -->
    <div v-if="isLoadingData" class="text-center py-5">
      <b-spinner variant="primary" label="Loading..." style="width: 3rem; height: 3rem;"></b-spinner>
      <p class="mt-3 h5">Loading stakeholder information...</p>
    </div>

    <!-- Error Loading Data -->
    <b-alert v-else-if="loadingError" show variant="danger" class="mt-3">
      <h4>Error Loading Stakeholder</h4>
      <p>{{ loadingError }}</p>
      <b-button @click="loadStakeholderData" variant="outline-danger">Try Again</b-button>
    </b-alert>

    <!-- Main Form -->
    <b-card v-else>
      <b-row>
        <div class="col-12 mt-16">
          <b-form @submit.prevent="updateStakeholder" v-if="show">
            <!-- Section Heading -->
            <div style="background-color: rgb(97, 116, 152); height: 32px; border-radius: 4px;">
              <h4 style="color: rgb(223, 227, 238); margin-left: 5px; font-weight: bold;">
                Update Stakeholder Information
              </h4>
            </div>

            <div class="row">
              <!-- Name -->
              <div class="col-md-4 col-12">
                <b-form-group label="Name:" label-class="font-weight-bold">
                  <b-form-input v-model="name" :state="nameState"required :disabled="isSubmitting" />
                </b-form-group>
              </div>

              <!-- Email -->
              <div class="col-md-4 col-12">
                <b-form-group label="Email:" label-class="font-weight-bold">
                  <b-form-input type="email" v-model="email" :state="emailState" :disabled="isSubmitting" />
                </b-form-group>
              </div>

              <!-- Phone -->
              <div class="col-md-4 col-12">
                <b-form-group label="Phone Number:" label-class="font-weight-bold">
                  <b-form-input v-model="phone" :state="phoneState" :disabled="isSubmitting" />
                </b-form-group>
              </div>

              <!-- Address -->
              <div class="col-md-4 col-12">
                <b-form-group label="Address:" label-class="font-weight-bold">
                  <b-form-input v-model="address" :state="addressState" :disabled="isSubmitting" />
                </b-form-group>
              </div>

              <!-- Type -->
              <div class="col-md-4 col-12">
                <b-form-group label="Type:" label-class="font-weight-bold">
                  <b-form-select v-model="type" :state="typeState" :options="types" required :disabled="isSubmitting" />
                </b-form-group>
              </div>

              <!-- Share Percentage -->
              <div class="col-md-4 col-12">
                <b-form-group label="Share Percentage (%):" label-class="font-weight-bold">
                  <b-form-input type="number" step="0.01" v-model="share_percentage" :state="sharePercentageState" :disabled="isSubmitting" />
                </b-form-group>
              </div>

              <!-- Legal Document -->
              <b-col cols="12" md="6">
                <b-form-group label="Legal Document:" label-class="font-weight-bold" description="Upload PDF/DOCX (Max 10MB)">
                  <!-- Current document -->
                  <div v-if="editedStakeholder.legal_document && !legalPreview" class="mb-2">
                    <small class="text-muted">Current document:</small><br>
                    <a :href="'https://wossdb.wossautoid.com/' + editedStakeholder.legal_document" target="_blank">
                      <i class="fas fa-file-pdf mr-2 text-danger"></i> View Current Document
                    </a>
                  </div>

                  <!-- Upload Area -->
                  <div class="border rounded p-3 text-center"
                       style="cursor: pointer; background: #f9f9f9;"
                       @drop.prevent="onLegalDrop"
                       @dragover.prevent
                       @click="$refs.legalInput.click()">
                    <div v-if="!legalPreview">
                      <i class="fas fa-cloud-upload-alt fa-2x text-muted"></i>
                      <p class="mb-0">Drag & Drop or Click to Upload</p>
                    </div>
                    <div v-else>
                      <i class="fas fa-file-alt fa-2x text-primary"></i>
                      <p class="mb-0">{{ legalPreview.name }}</p>
                    </div>
                    <input ref="legalInput" type="file" accept=".pdf,.doc,.docx"
                           class="d-none" @change="onLegalDocumentChange" />
                  </div>
                  <small class="text-muted">Leave empty to keep current document</small>
                  <b-form-invalid-feedback v-if="legalError" class="d-block text-danger">
                    {{ legalError }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>

              <!-- Profile Picture -->
              <b-col cols="12" md="6">
                <b-form-group label="Profile Picture:" label-class="font-weight-bold" description="Recommended size: 300x300px (JPG/PNG)">
                  <!-- Current image -->
                  <div v-if="editedStakeholder.image && !imagePreview" class="mb-2">
                    <small class="text-muted">Current image:</small><br>
                    <img :src="'https://wossdb.wossautoid.com/' + editedStakeholder.image"
                         alt="Current Picture"
                         class="img-thumbnail"
                         style="max-height: 120px;" />
                  </div>

                  <!-- Upload Area -->
                  <div class="border rounded p-3 text-center"
                       style="cursor: pointer; background: #f9f9f9;"
                       @drop.prevent="onImageDrop"
                       @dragover.prevent
                       @click="$refs.profileInput.click()">
                    <div v-if="!imagePreview">
                      <i class="fas fa-cloud-upload-alt fa-2x text-muted"></i>
                      <p class="mb-0">Drag & Drop or Click to Upload</p>
                    </div>
                    <div v-else>
                      <img :src="imagePreview"
                           alt="Preview"
                           class="img-thumbnail"
                           style="max-height: 120px;" />
                    </div>
                    <input ref="profileInput" type="file" accept="image/png, image/jpeg"
                           class="d-none" @change="onProfilePictureChange" />
                  </div>
                  <small class="text-muted">Leave empty to keep current image</small>
                  <b-form-invalid-feedback v-if="profileError" class="d-block text-danger">
                    {{ profileError }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>

              <!-- Notes -->
              <div class="col-md-12 col-12">
                <b-form-group label="Notes:" label-class="font-weight-bold">
                  <b-form-textarea v-model="notes" :state="notesState" rows="3" :disabled="isSubmitting" />
                </b-form-group>
              </div>
            </div>

            <!-- Submit Buttons -->
            <div class="d-flex align-items-center">
              <b-button type="submit" variant="primary" class="mr-3"
                        :disabled="isSubmitting || !isFormValid">
                <b-spinner v-if="isSubmitting" small class="mr-2"></b-spinner>
                <span v-if="isSubmitting">Updating...</span>
                <span v-else>Update Stakeholder</span>
              </b-button>
              <!-- <b-button variant="secondary" :disabled="isSubmitting" @click="cancelEdit">
                Cancel
              </b-button> -->
            </div>
          </b-form>
        </div>
      </b-row>
    </b-card>
  </div>
</template>

<script>
import {
  BRow, BCol, BCard, BButton, BForm, BFormGroup, BFormInput,
  BFormSelect, BFormTextarea, BSpinner, BAlert, BFormInvalidFeedback
} from "bootstrap-vue";
import axios from "../../../axios";

export default {
  data() {
    return {
      show: true,
      isLoadingData: true,
      isSubmitting: false,
      successMessage: "",
      errorMessage: "",
      loadingError: null,

      // Stakeholder fields
      name: "", email: "", phone: "", address: "",
      type: "investor", share_percentage: "", notes: "",
      image: "", legal_document: "",

      // Previews & errors
      imagePreview: null,
      legalPreview: null,
      profileError: "",
      legalError: "",

      editedStakeholder: {},
      types: [
        { value: "client", text: "Client" },
        { value: "vendor", text: "Vendor" },
        { value: "investor", text: "Investor" },
        { value: "partner", text: "Partner" },
        { value: "employee", text: "Employee" },
      ],
    };
  },

  computed: {
    isFormValid() {
      return this.name.trim().length > 0 && this.type.length > 0;
    },
      nameState() {
        return this.validateField(this.name);
      },
      emailState() {
        return this.validateField(this.email);
      },
      phoneState() {
        return this.validateField(this.phone);
      },
      addressState() {
        return this.validateField(this.address);
      },
      typeState() {
        return this.validateField(this.type);
      },
      sharePercentageState() {
        return this.validateField(this.share_percentage);
      },
      notesState() {
        return this.validateField(this.notes);
      }
  },

  components: {
    BRow, BCol, BCard, BButton, BForm, BFormGroup, BFormInput,
    BFormSelect, BFormTextarea, BSpinner, BAlert, BFormInvalidFeedback
  },

  created() {
    this.loadStakeholderData();
  },

  methods: {
    validateField(value) {
      if (value === null || value === undefined) return null;
      if (typeof value === "string") {
        return value.trim().length > 0 ? true : false;
      }
    },
    async loadStakeholderData() {
      try {
        this.isLoadingData = true;
        const id = this.$route.params.id;
        const response = await axios.get(`stakeholders/${id}`);
        this.editedStakeholder = response.data.data;

        // Populate form
        this.name = this.editedStakeholder.name || "";
        this.email = this.editedStakeholder.email || "";
        this.phone = this.editedStakeholder.phone || "";
        this.address = this.editedStakeholder.address || "";
        this.type = this.editedStakeholder.type || "investor";
        this.share_percentage = this.editedStakeholder.share_percentage || "";
        this.notes = this.editedStakeholder.notes || "";
        this.image = this.editedStakeholder.image || "";
        this.legal_document = this.editedStakeholder.legal_document || "";
        this.isLoadingData = false;
      } catch (error) {
        this.loadingError = "Failed to load stakeholder information. Please try again.";
        this.isLoadingData = false;
      }
    },

    async updateStakeholder() {
      if (!this.isFormValid) return;
      try {
        this.isSubmitting = true;
        this.errorMessage = "";
        this.successMessage = "";

        const formData = new FormData();
        formData.append("name", this.name);
        formData.append("email", this.email);
        formData.append("phone", this.phone);
        formData.append("address", this.address);
        formData.append("type", this.type);
        formData.append("share_percentage", this.share_percentage);
        formData.append("notes", this.notes);

        if (this.image instanceof File) formData.append("image", this.image);
        if (this.legal_document instanceof File) formData.append("legal_document", this.legal_document);

        await axios.post(`stakeholdersUpdate/${this.editedStakeholder.id}`, formData);

        this.successMessage = "Stakeholder updated successfully!";
        this.$bvToast.toast("Stakeholder updated successfully!", {
          title: "Success", variant: "primary", solid: true,
          toaster: "b-toaster-top-right", autoHideDelay: 5000,
        });
      } catch (error) {
        this.errorMessage = "Error updating stakeholder. Please try again.";
      } finally {
        this.isSubmitting = false;
      }
    },

    // File handlers
    onProfilePictureChange(e) {
      const file = e.target.files[0];
      if (!file) return;
      if (!["image/jpeg", "image/png"].includes(file.type)) {
        this.profileError = "Only JPG/PNG allowed."; return;
      }
      if (file.size > 5 * 1024 * 1024) {
        this.profileError = "Max size is 5MB."; return;
      }
      this.image = file;
      this.imagePreview = URL.createObjectURL(file);
      this.profileError = "";
    },
    onImageDrop(e) {
      const file = e.dataTransfer.files[0];
      this.onProfilePictureChange({ target: { files: [file] } });
    },

    onLegalDocumentChange(e) {
      const file = e.target.files[0];
      if (!file) return;
      if (!["application/pdf",
            "application/msword",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document"].includes(file.type)) {
        this.legalError = "Only PDF/DOC/DOCX allowed."; return;
      }
      if (file.size > 10 * 1024 * 1024) {
        this.legalError = "Max size is 10MB."; return;
      }
      this.legal_document = file;
      this.legalPreview = file;
      this.legalError = "";
    },
    onLegalDrop(e) {
      const file = e.dataTransfer.files[0];
      this.onLegalDocumentChange({ target: { files: [file] } });
    },

    cancelEdit() {
      this.$router.push({ name: "ListStakeholder" });
    },
  },
};
</script>

<style scoped>
.form-control-file { padding: 0.375rem 0; }
.alert { margin-bottom: 1rem; }
small.form-text { margin-top: 0.25rem; }
</style>
